import React, { useCallback, useEffect, useState } from 'react';
import validSvc from '../../../core/validSvc';
import ValidInputText from './ValidInputText';
import Button from '../Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark } from '@fortawesome/free-solid-svg-icons';

// VIEW MODEL
export function validPropMultiTextFieldVM(
    key: string,
    lbl?: string,
    val?: string[],
    err?: string,
): ValidMultiFieldTextProp {
    return {
        key: key,
        lbl: !!lbl ? lbl : '',
        val: !!val ? val : [],
        err: !!err ? err : '',
    };
}

export type ValidMultiFieldTextProp = {
    key: string;
    lbl: string;
    val: string[];
    err: string;
};

type ValidMultiFieldTextProps = {
    prop: ValidMultiFieldTextProp;
    onChange: (prop: string, val: string[]) => void;
    disabled?: boolean;
    maxlen?: number;
    minlen?: number;
};

const ValidMultiFieldText = ({
    prop,
    onChange,
    disabled = false,
    maxlen = undefined,
    minlen = undefined,
}: ValidMultiFieldTextProps): JSX.Element => {
    const [valueToAdd, setValueToAdd] = useState<string>('');
    const [internalError, setInternalError] = useState<string>('');
    const [internalValues, setInternalValues] = useState<Map<any, any>>(new Map());

    useEffect(() => {
        setInternalError(prop.err);
        setInternalValues(new Map(prop.val.map(v => [v, v])));

        return (): void => {
            setValueToAdd('');
            setInternalValues(new Map());
            setInternalError('');
        };
    }, [prop.err, prop.val]);

    const valueToAddProp = {
        key: 'valueToAdd',
        lbl: prop.lbl,
        val: valueToAdd,
        err: '',
    };

    const handleUpdateNewValue = useCallback(
        (_prop: string, val: string): void => {
            if (internalError) {
                setInternalError('');
            }
            setValueToAdd(val);
        },
        [setInternalError, internalError],
    );

    const handleSubmitNewValue = useCallback((): void => {
        const val = valueToAdd.trim();
        if (internalValues.has(val)) {
            setInternalError('This value has already been added.');
            return;
        }
        if (!!minlen && validSvc.isInteger(minlen) && val.length < minlen) {
            setInternalError(`minimum ${minlen} characters`);
            return;
        }

        const newMap = internalValues;
        newMap.set(val, val);
        setInternalError('');
        setInternalValues(newMap);
        setValueToAdd('');
        onChange(prop.key, [...prop.val, val]);
    }, [valueToAdd, internalValues, onChange, prop, minlen]);

    const handleDeleteValue = useCallback(
        (val: string) => {
            const newMap = new Map(internalValues);
            newMap.delete(val);
            setInternalValues(newMap);
            onChange(prop.key, [...newMap.values()]);
        },
        [internalValues, onChange, prop.key],
    );

    const handleChangeExistingValue = useCallback(
        (key: string, newVal: string) => {
            const newMap = new Map(internalValues);
            newMap.set(key, newVal);

            setInternalValues(newMap);
        },
        [internalValues, setInternalValues],
    );

    const arrayInternalValues = Array.from(internalValues);

    return (
        <div className="component-valid-multi-field-text">
            <div className="insert-new-field-container">
                <ValidInputText
                    prop={valueToAddProp}
                    onChange={handleUpdateNewValue}
                    placeholder="Enter new value..."
                    fullWidth={true}
                    hideError
                    maxlen={maxlen}
                    disabled={disabled}
                />
                <Button
                    onClick={handleSubmitNewValue}
                    disabled={valueToAdd.trim().length === 0 || disabled}
                >
                    Add
                </Button>
                <div className="error-message">{internalError}</div>
            </div>
            {internalValues.size && internalValues.size > 0 ? (
                <div className="editable-fields-container">
                    <p className="caption">Total Items: {internalValues.size}</p>
                    {arrayInternalValues
                        ? arrayInternalValues.map(([key, val]) => (
                              <div
                                  className="editable-field"
                                  key={`${key}-item-container-${val}`}
                              >
                                  <ValidInputText
                                      prop={{
                                          key: `${key}-item-${val}`,
                                          val: val,
                                          err: '',
                                      }}
                                      onChange={() => handleChangeExistingValue(key, val)}
                                      hideError
                                      disabled={disabled}
                                  />
                                  {disabled ? null : (
                                      <FontAwesomeIcon
                                          icon={faCircleXmark}
                                          className="action-delete"
                                          onClick={() => handleDeleteValue(val)}
                                      />
                                  )}
                              </div>
                          ))
                        : null}
                </div>
            ) : null}
        </div>
    );
};

export default ValidMultiFieldText;
